// src/components/sections/HeroSection.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  CheckCircle,
  Eye,
  Sparkles,
  Copy,
  Settings,
  Info,
  ChevronDown,
  Edit2,
  Lock,
  Unlock,
  Users,
  BarChartHorizontal,
  Share2,
  ShieldCheck,
  Zap,
  Link as LinkIcon,
  ExternalLink,
} from 'lucide-react';
import {
  AgentData_V10,
  AgentHubPageViewMode,
  OwnerContext,
  TLicenseTypeV10,
  TAgentOperationalStatusV10,
  TAgentVisibilityV10,
} from '@dopomogai/types';
import { Card } from '@dopomogai/ui';
import { Badge } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { IconButton } from '@dopomogai/ui';
import { VisualElement } from '@dopomogai/ui';
import { Tooltip } from '@dopomogai/ui';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { renderIcon } from '@dopomogai/utils';

interface HeroSectionProps {
  agentData: AgentData_V10;
  viewMode: AgentHubPageViewMode;
  ownerContext?: OwnerContext;
  onAction: (actionType: string, payload?: any) => void;
  id?: string;
}

// --- Constants for dropdowns ---
const operationalStatusOptions: { value: TAgentOperationalStatusV10; labelKey: string; icon: React.ReactNode }[] = [
  { value: 'Active & Optimized', labelKey: 'operationalStatus.active', icon: <Sparkles size={16} className="text-green-600" /> },
  { value: 'In Beta Testing', labelKey: 'operationalStatus.beta', icon: <Zap size={16} className="text-blue-600" /> },
  { value: 'Development Paused', labelKey: 'operationalStatus.paused', icon: <BarChartHorizontal size={16} className="text-amber-600" /> },
  { value: 'Archived', labelKey: 'operationalStatus.archived', icon: <Lock size={16} className="text-slate-500" /> },
  { value: 'Private Draft', labelKey: 'operationalStatus.draft', icon: <Edit2 size={16} className="text-purple-600" /> },
];

const visibilityStatusOptions: { value: TAgentVisibilityV10; labelKey: string; icon: React.ReactNode }[] = [
  { value: 'Publicly Listed', labelKey: 'visibilityStatus.public', icon: <Eye size={16} className="text-green-600" /> },
  { value: 'Unlisted (Direct Link Only)', labelKey: 'visibilityStatus.unlisted', icon: <LinkIcon size={16} className="text-amber-600" /> },
  { value: 'Private (Owner & Admins Only)', labelKey: 'visibilityStatus.private', icon: <Users size={16} className="text-red-600" /> },
];

const licenseInfoMap: Record<TLicenseTypeV10, { labelKey: string; descriptionKey: string; icon: React.ReactNode }> = {
  'Proprietary EULA': { labelKey: 'license.proprietary', descriptionKey: 'license.proprietaryDesc', icon: <ShieldCheck size={16} className="text-red-500" /> },
  'Customizable Source Available': { labelKey: 'license.sourceAvailable', descriptionKey: 'license.sourceAvailableDesc', icon: <Settings size={16} className="text-blue-500" /> },
  'MIT': { labelKey: 'license.mit', descriptionKey: 'license.mitDesc', icon: <Unlock size={16} className="text-green-500" /> },
  'Apache 2.0': { labelKey: 'license.apache', descriptionKey: 'license.apacheDesc', icon: <Unlock size={16} className="text-green-500" /> },
  'GPLv3': { labelKey: 'license.gpl3', descriptionKey: 'license.gpl3Desc', icon: <Copy size={16} className="text-purple-500" /> },
};


// --- OwnerControlDropdown Component ---
interface OwnerControlDropdownProps {
  label: string;
  currentValue: string;
  currentIcon: React.ReactNode;
  options: { value: string; labelKey: string; icon: React.ReactNode }[];
  onSelect: (value: string) => void;
  tooltipText: string;
  t: (key: string, options?: any) => string;
}

const OwnerControlDropdown: React.FC<OwnerControlDropdownProps> = ({
  label, currentValue, currentIcon, options, onSelect, tooltipText, t
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
      <Tooltip text={tooltipText} position="top" disabled={isOpen}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between text-left px-3 py-2 bg-white border-2 border-slate-300 rounded-lg hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors shadow-sm"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="flex items-center">
            {currentIcon}
            <span className="ml-2 text-sm font-semibold text-slate-700">{currentValue}</span>
          </span>
          <ChevronDown size={18} className={`text-slate-400 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </Tooltip>
      <AnimatePresence>
        {isOpen && (
          <motion.ul
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 mt-1 w-full bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-20 border-2 border-black overflow-hidden"
            role="listbox"
          >
            {options.map(option => (
              <li key={option.value}>
                <button
                  onClick={() => { onSelect(option.value); setIsOpen(false); }}
                  className={clsx(
                    "w-full text-left px-3 py-2.5 text-sm flex items-center hover:bg-slate-100 transition-colors",
                    option.value === currentValue ? "font-semibold text-emerald-600 bg-emerald-50" : "text-slate-700"
                  )}
                  role="option"
                  aria-selected={option.value === currentValue}
                >
                  {option.icon}
                  <span className="ml-2">{t(option.labelKey)}</span>
                  {option.value === currentValue && <CheckCircle size={14} className="ml-auto text-emerald-500" />}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};


// --- HeroSection Component ---
export const HeroSection = React.forwardRef<HTMLDivElement, HeroSectionProps>(
  ({ agentData, viewMode, ownerContext, onAction, id }, ref) => {
    const { t } = useTranslation('agent-common');
    const isOwnerView = viewMode === 'owner_dashboard';

    const handleStatusChange = (newStatus: string) => {
      onAction('CHANGE_OPERATIONAL_STATUS', { newStatus: newStatus as TAgentOperationalStatusV10 });
    };

    const handleVisibilityChange = (newVisibility: string) => {
      onAction('CHANGE_VISIBILITY', { newVisibility: newVisibility as TAgentVisibilityV10 });
    };

    const currentOperationalStatusObj = operationalStatusOptions.find(opt => opt.value === agentData.operationalStatus) || operationalStatusOptions[0];
    const currentVisibilityStatusObj = visibilityStatusOptions.find(opt => opt.value === agentData.visibilityStatus) || visibilityStatusOptions[0];
    const currentLicenseObj = licenseInfoMap[agentData.licenseType] || licenseInfoMap['Proprietary EULA'];

    const textVariants = {
      hidden: { opacity: 0, y: 20 },
      visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.07, duration: 0.4, ease: "easeOut" }
      })
    };

    return (
      <Card
        id={id}
        ref={ref}
        className="md:p-0 overflow-hidden border-black"
        noPadding
      >
        <div className="flex flex-col lg:flex-row bg-white">
          {/* Left Column - Visual */}
          <div className="lg:w-2/5 xl:w-5/12 relative flex items-center justify-center p-6 md:p-8 lg:p-10 bg-slate-50 border-b-2 border-black lg:border-b-0 lg:border-r-2 lg:border-black">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1, ease: [0.6, -0.05, 0.01, 0.99] }}
              className="aspect-square w-full max-w-md lg:max-w-none relative group border-2 border-black rounded-xl overflow-hidden shadow-[8px_8px_0_0_rgb(17,24,39)]"
            >
              <VisualElement
                visual={agentData.heroVisual}
                //showChatButton={!isOwnerView}
                //onChatClick={() => onAction('START_CHAT', { agentId: agentData.id })}
                className="transform transition-transform duration-500 ease-out group-hover:scale-105"
              />
              {isOwnerView && (
                <div className="absolute top-3 right-3">
                  <IconButton
                    icon={<Edit2 size={16} />}
                    ariaLabelKey="heroSection.editVisualAria"
                    // --- FIX #1 ---
                    // The `VisualElementV10` type is a union and doesn't guarantee a `.url` property.
                    // Passing the whole visual object is safer and more flexible for the handler.
                    onClick={() => onAction('EDIT_HERO_VISUAL', { visual: agentData.heroVisual })}
                    variant="secondary"
                    size="sm"
                    className="!rounded-lg bg-white/80 backdrop-blur-sm hover:bg-white"
                    tooltipKey="heroSection.editVisualTooltip"
                  />
                </div>
              )}
            </motion.div>
          </div>

          {/* Right Column - Info */}
          <div className="lg:w-3/5 xl:w-7/12 p-6 md:p-10 lg:p-12 flex flex-col justify-center">
            {isOwnerView && ownerContext?.userName && (
              <motion.p custom={0} variants={textVariants} initial="hidden" animate="visible" className="text-sm text-slate-500 mb-4 font-medium">
                <Tooltip text={`${t('heroSection.lastLoginLabel')}: ${ownerContext.lastLogin ? new Date(ownerContext.lastLogin).toLocaleString() : t('common.notAvailableAbbr')}`}>
                  <span>{t('heroSection.welcomeBack', { userName: ownerContext.userName })}</span>
                </Tooltip>
              </motion.p>
            )}
            <div className="flex justify-between items-start mb-1 sm:mb-2">
              <motion.h1 custom={1} variants={textVariants} initial="hidden" animate="visible" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight">
                {agentData.name}
              </motion.h1>
              {isOwnerView && (
                <IconButton icon={<Edit2 size={18} />} ariaLabelKey="heroSection.editIdentityAria" onClick={() => onAction('EDIT_AGENT_IDENTITY', null)} variant="ghost" size="md" className="ml-3 shrink-0 !rounded-lg text-slate-400 hover:text-emerald-600" tooltipKey="heroSection.editIdentityTooltip" />
              )}
            </div>
            <motion.p custom={2} variants={textVariants} initial="hidden" animate="visible" className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-5 sm:mb-6">
              {agentData.tagline}
            </motion.p>
            <motion.div custom={3} variants={textVariants} initial="hidden" animate="visible" className="text-sm text-slate-500 mb-5 sm:mb-6 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span>{t('heroSection.byLabel')}:</span>
              {agentData.creatorProfileUrl ? (
                <a href={agentData.creatorProfileUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-teal-600 hover:text-teal-700 hover:underline flex items-center gap-1" onClick={(e) => { e.preventDefault(); onAction('VIEW_CREATOR_PROFILE', { url: agentData.creatorProfileUrl }); }}>
                  {agentData.creatorName} <ExternalLink size={12} className="opacity-70" />
                </a>
              ) : ( <span className="font-semibold text-slate-700">{agentData.creatorName}</span> )}
              <span className="text-slate-400">•</span>
              <span>{t('heroSection.versionLabel')}:</span>
              <span className="font-semibold text-slate-700">{agentData.version}</span>
            </motion.div>


            <motion.div
              custom={4} variants={textVariants} initial="hidden" animate="visible"
              className="bg-emerald-50 border-2 border-emerald-500 rounded-xl p-4 mb-6 sm:mb-8 flex items-start gap-3 shadow-[3px_3px_0_0_rgba(5,150,105,0.4)]"
            >
              <CheckCircle size={32} className="text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-emerald-800 font-medium leading-relaxed">
                  <span className="font-bold">
                    {t('heroSection.ethicalContributionPart1', { percentage: agentData.ethicalContributionPercentage })}
                  </span>
                  {' '}
                  {t('heroSection.ethicalContributionPart2', { target: agentData.socialImpactTargetChosenByOwner || t('heroSection.defaultSocialGoodFund') })}.
                  {' '}
                  <a
                    href={agentData.ethicalAIPlatformImpactUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold hover:underline text-emerald-600 hover:text-emerald-700"
                    onClick={(e) => { e.preventDefault(); onAction('LEARN_MORE_ETHICAL_IMPACT', { url: agentData.ethicalAIPlatformImpactUrl }); }}
                  >
                    {t('common.learnMore')}
                  </a>
                </p>
              </div>
            </motion.div>

            <motion.p
              custom={5} variants={textVariants} initial="hidden" animate="visible"
              className="text-md text-slate-600 leading-relaxed mb-6 sm:mb-8"
            >
              {agentData.shortDescription}
            </motion.p>

            {isOwnerView && (
              <motion.div custom={6} variants={textVariants} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4 mb-6 sm:mb-8 bg-slate-100 rounded-xl p-5 border-2 border-black shadow-[3px_3px_0_0_black]">
                <OwnerControlDropdown label={t('heroSection.operationalStatusLabel')} currentValue={t(currentOperationalStatusObj.labelKey)} currentIcon={currentOperationalStatusObj.icon} options={operationalStatusOptions} onSelect={handleStatusChange} tooltipText={t('heroSection.operationalStatusTooltip')} t={t} />
                <OwnerControlDropdown label={t('heroSection.visibilityLabel')} currentValue={t(currentVisibilityStatusObj.labelKey)} currentIcon={currentVisibilityStatusObj.icon} options={visibilityStatusOptions} onSelect={handleVisibilityChange} tooltipText={t('heroSection.visibilityTooltip')} t={t} />
                <div className="sm:col-span-2 lg:col-span-1">
                  <p className="text-xs font-medium text-slate-500 mb-1">{t('heroSection.licenseLabel')}</p>
                  <div className="flex items-center px-3 py-2.5 bg-white border-2 border-slate-300 rounded-lg shadow-sm">
                    {React.cloneElement(currentLicenseObj.icon as React.ReactElement, { className: "mr-2" })}
                    <p className="text-sm font-semibold text-slate-700">{t(currentLicenseObj.labelKey)}</p>
                    <Tooltip text={t(currentLicenseObj.descriptionKey) || t('common.notAvailableAbbr')} position="top">
                      <button onClick={() => onAction('VIEW_LICENSE_INFO', { title: t(currentLicenseObj.labelKey), info: t(currentLicenseObj.descriptionKey) })} className="ml-auto text-slate-400 hover:text-slate-600 p-1 -mr-1" aria-label={t('heroSection.licenseInfoAria', { licenseType: t(currentLicenseObj.labelKey)})}>
                        <Info size={16} />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              </motion.div>
            )}

            {agentData.heroBadges && agentData.heroBadges.length > 0 && (
              <motion.div
                custom={7} variants={textVariants} initial="hidden" animate="visible"
                className="flex flex-wrap gap-2.5 sm:gap-3 mb-8 sm:mb-10"
              >
                {agentData.heroBadges.map((badge, index) => (
                  <Badge
                    key={`${badge.text || badge.textKey}-${index}`}
                    text={badge.text}
                    textKey={badge.textKey}
                    icon={renderIcon(badge.icon, {size: 14})}
                    tooltip={badge.tooltip}
                    tooltipKey={badge.tooltipKey}
                    url={badge.url}
                    accentColor={badge.accentColor}
                    onClick={badge.actionType ? () => onAction(badge.actionType!, badge.actionPayload) : undefined}
                    className="transform hover:scale-105 transition-transform duration-150 !focus-visible:ring-slate-400 !focus-visible:ring-offset-2"
                  />
                ))}
              </motion.div>
            )}

            <motion.div custom={8} variants={textVariants} initial="hidden" animate="visible" className="mt-auto flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              {isOwnerView ? (
                <>
                  <Button textKey="heroSection.ownerCta.settings" variant="primary" icon={<Settings size={18} />} onClick={() => onAction('OPEN_AGENT_SETTINGS_DASHBOARD', null)} className="flex-1 sm:flex-none" ariaLabelKey="heroSection.ownerCta.settingsAria" />
                  <Button textKey="heroSection.ownerCta.preview" variant="secondary" icon={<Eye size={18} />} onClick={() => onAction('PREVIEW_PUBLIC_CV', null)} className="flex-1 sm:flex-none" ariaLabelKey="heroSection.ownerCta.previewAria" />
                  <Button textKey="heroSection.ownerCta.architectAi" variant="outline" icon={<Sparkles size={18} />} onClick={() => onAction('OPEN_ARCHITECT_AI_CHAT', null)} className="flex-1 sm:flex-none" ariaLabelKey="heroSection.ownerCta.architectAiAria" />
                </>
              ) : (
                <>
                  <Button textKey="heroSection.publicCta.getStarted" variant="primary" size="lg" onClick={() => onAction('GET_STARTED_INTEGRATE', { agentId: agentData.id })} className="flex-1 sm:flex-auto !py-3.5" ariaLabelKey="heroSection.publicCta.getStartedAria" />
                  <Button 
                    textKey="heroSection.publicCta.viewDemo" 
                    variant="secondary" 
                    size="lg" 
                    // --- FIX #2 ---
                    // The property `primaryDemoElement` does not exist on the type.
                    // The fix is to safely access the first item from the `demoElements` array instead.
                    onClick={() => onAction('VIEW_INTERACTIVE_DEMO', { demoElement: agentData.demoSectionPublic?.demoElements?.[0] })} 
                    className="flex-1 sm:flex-auto !py-3.5" 
                    ariaLabelKey="heroSection.publicCta.viewDemoAria" 
                  />
                  {agentData.allowCopyingIfApplicable && (
                    <Button textKey="heroSection.publicCta.copyAgent" variant="outline" size="lg" icon={<Copy size={16} />} onClick={() => onAction('COPY_AGENT', { agentId: agentData.id })} className="flex-1 sm:flex-auto !py-3.5" ariaLabelKey="heroSection.publicCta.copyAgentAria" />
                  )}
                </>
              )}
            </motion.div>
          </div>
        </div>
      </Card>
    );
  }
);
HeroSection.displayName = 'HeroSection';